import { AppBar, Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Toolbar, Typography, Chip, OutlinedInput, Stack, Paper } from '@mui/material'
import React, { useEffect, useState } from 'react'
import LoadingBackdrop from '../../Common/LoadingBackdrop'
import { Cancel, Save } from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOneAdmin, saveAdmin } from '../../../actions/adminsActions';
import { fetchAllRoles } from '../../../actions/rolesActions';
import MainContentContainer from '../../MainContentContainer/MainContentContainer';

export default function AdminFormPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const [admin, setAdmin] = useState({
        admin_id: 0,
        admin_name: '',
        admin_email: '',
        admin_password: '',
        admin_password_confirmation: '',
        roles: []
    });

    const loading = useSelector(state => state.admins.loading);
    const adminStore = useSelector(state => state.admins.admin);
    const roles = useSelector(state => state.roles.roles);

    useEffect(() => {
        dispatch(fetchAllRoles());
    }, []);

    useEffect(() => {
        if (!id) return;
        // Only fetch if we don't already have the correct admin in the store
        if (!adminStore || adminStore.admin_id !== parseInt(id)) {
            dispatch(fetchOneAdmin(id));
            return;
        }
        // Store has the correct admin — populate the form
        setAdmin({
            admin_id: adminStore.admin_id || 0,
            admin_name: adminStore.admin_name || '',
            admin_email: adminStore.admin_email || '',
            admin_password: '',
            admin_password_confirmation: '',
            roles: adminStore.roles ? adminStore.roles.map(role => role.role_id) : []
        });
    }, [id, adminStore]);

    const handleSave = e => {
        e.preventDefault();
        if (admin.admin_id === 0 && (admin.admin_password !== admin.admin_password_confirmation)) {
            Swal.fire({
                title: 'Password Mismatch',
                text: 'Password and password confirmation do not match',
                type: 'error'
            })
            return;
        }

        dispatch(saveAdmin(admin, () => navigate('/admins')));
    }

    const handleChange = e => {
        setAdmin({
            ...admin,
            [e.target.name]: e.target.value
        })
    }

    const handleRoleChange = (event) => {
        const {
            target: { value },
        } = event;
        setAdmin({
            ...admin,
            roles: typeof value === 'string' ? value.split(',') : value,
        });
    };


    return (
        <MainContentContainer component="form" onSubmit={handleSave}>
            <LoadingBackdrop open={loading} />
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        {admin.admin_id > 0 ? 'Edit' : 'Create'} Admin user
                    </Typography>
                    <Button
                        color="success"
                        type="submit"
                        startIcon={<Save />}
                        variant="contained"
                        sx={{ marginRight: 2 }}
                        disabled={loading}
                    >
                        Save
                    </Button>
                    <Button
                        color="error"
                        component={Link}
                        to={`/admins`}
                        startIcon={<Cancel />}
                        variant="contained"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                </Toolbar>
            </AppBar>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        <TextField
                            fullWidth
                            label="Name"
                            name="admin_name"
                            value={admin.admin_name || ''}
                            onChange={handleChange}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Email"
                            name="admin_email"
                            type="email"
                            value={admin.admin_email || ''}
                            onChange={handleChange}
                            required
                        />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        <TextField
                            fullWidth
                            label="Password"
                            name="admin_password"
                            type="password"
                            autoComplete="new-password"
                            value={admin.admin_password || ''}
                            onChange={handleChange}
                            required={admin.admin_id === 0}
                        />
                        <TextField
                            fullWidth
                            label="Confirm Password"
                            name="admin_password_confirmation"
                            type="password"
                            autoComplete="new-password"
                            value={admin.admin_password_confirmation || ''}
                            onChange={handleChange}
                            required={admin.admin_id === 0}
                        />
                    </Stack>
                    <FormControl fullWidth>
                        <InputLabel>Roles</InputLabel>
                        <Select
                            multiple
                            value={admin.roles}
                            onChange={handleRoleChange}
                            input={<OutlinedInput label="Roles" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((roleId) => {
                                        const role = roles.find(r => r.role_id === roleId);
                                        return (
                                            <Chip
                                                key={roleId}
                                                label={role ? role.role_name : ''}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {roles.map((role) => (
                                <MenuItem key={role.role_id} value={role.role_id}>
                                    {role.role_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Paper>
            </Box>
        </MainContentContainer>
    )
}
